// Copyright 2023, Durable.

import React, { ReactElement, useState } from "react";
import {
  ApiError,
  Body_update_user_pic,
  UserService
} from "@durable/durable-client";
import { Button, Input } from "@mui/material";
import { fileToBlob } from "../../layouts/UserPortal";
import { logging } from "../../logging";

/**
 * A single setting, largely a visual wrapping for the toggle. The toggle
 * may be very simple or very complex depending, so this is built to provide
 * flexibility to potentially complex setting toggles in the future.
 *
 * Interactable is any ReactElement used to set a setting, down the road it'll
 * need to be generrated.
 */
function SingleSetting(props: {
  key: number;
  settingName: string;
  settingDesc: string;
  interactable: () => ReactElement;
}) {
  const { settingName, settingDesc, interactable } = props;
  return (
    <div>
      <div className="my-5 grid grid-cols-2 w-full">
        <div className="w-full">
          <div className="text-base">{settingName}</div>
          <div className="text-sm text-coolGrey-400 dark:text-coolGrey-800">
            {settingDesc}
          </div>
        </div>
        <div className="w-full flex justify-end">{interactable()}</div>
      </div>
    </div>
  );
}

/**
 * The setting to change a user's password.
 * Currently defined manually here, however this is a temporary construct-
 * it will be replaced by a nice flat data model on the backend in the future.
 */
function ChangePasswordSetting(id: number) {
  /**
   * The actual fields to change your password. Will later be built from the
   * model.
   */
  function usePasswordInteractable() {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [buttonText, setButtonText] = useState("Change Password");

    /**
     * Hit the API and change the password as specified
     */
    function changePassword() {
      setButtonText("Loading...");
      UserService.updateUserPassword({
        current_password: currentPassword,
        password: newPassword
      })
        .then(() => setButtonText("Password Changed"))
        .catch(() => setButtonText("Error"));
    }
    return (
      <div className="flex flex-col">
        <Input
          placeholder="Current Password"
          type="password"
          required
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
        <Input
          placeholder="New Password"
          type="password"
          required
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <Button size="small" onClick={() => changePassword()}>
          {buttonText}
        </Button>
      </div>
    );
  }
  return (
    <SingleSetting
      key={id}
      settingName="Password"
      settingDesc="Change your Password"
      interactable={usePasswordInteractable}
    />
  );
}

/**
 * Hit up the API and update the user picture
 */
export async function updateAvatar(profilePicture: Body_update_user_pic) {
  await UserService.updateUserPic(profilePicture).catch((err: ApiError) =>
    logging.error(err.body)
  );
  window.location.reload();
}

/**
 * A setting to update your current profile picture. Can also be accomplished
 * from user avatar dropdown, for discoverability.
 */
function ChangeAvatarSetting(id: number) {
  /**
   * The upload button to choose a new profile picture
   */
  function uploadAvatar() {
    return (
      <div className="flex items-center">
        <input
          type="file"
          id="fileUpload"
          onChange={(event) => {
            updateAvatar(fileToBlob(event.target.files[0]));
          }}
          className="w-48"
          accept="image/png"
        />
      </div>
    );
  }
  return (
    <SingleSetting
      key={id}
      settingName="Change profile picture"
      settingDesc="Upload a profile picture to associate with your account"
      interactable={uploadAvatar}
    />
  );
}

/**
 * A single category under the settings panel.
 */
function SettingsCategory({
  categoryName,
  settingComponents
}: {
  categoryName: string;
  settingComponents: Array<(i) => ReactElement>;
}) {
  return (
    <div>
      <h3>{categoryName}</h3>
      <div>{settingComponents.map((component, i) => component(i))}</div>
    </div>
  );
}

/**
 * The main settings for the durable app.
 */
export function DurableSettings() {
  return (
    <div>
      <div className="max-w-xl">
        {/* <SettingsCategory
          categoryName="Blueprint Options"
          settingComponents={[
            ToggleStepsSetting,
            ToggleForewardExamplesSetting
          ]}
        /> */}
        <SettingsCategory
          categoryName="My Account"
          settingComponents={[ChangeAvatarSetting, ChangePasswordSetting]}
        />
      </div>
    </div>
  );
}

export default { DurableSettings };
